import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/lib/shared/auth";
import { useBetaFeatures } from "@/lib/shared/beta-features";
import {
  awardXP,
  checkAndAwardBadges,
  getBadgeDefinition,
  getProximityNudge,
  getUserGamification,
  LEVEL_LABELS,
  type UserGamification,
} from "@/lib/shared/gamification";
import { queryKeys } from "@/lib/shared/query-client";

export interface GamificationToastData {
  id: string;
  type: "level_up" | "badge" | "xp_bonus" | "proximity" | "streak_at_risk";
  title: string;
  description: string;
  icon: string;
}

export interface XPAnimationData {
  id: string;
  amount: number;
}

interface GamificationContextValue {
  /** Current gamification state (null if not loaded or no row yet) */
  gamification: UserGamification | null;
  /** Track an XP-earning action. Awards XP, checks badges, shows toasts. */
  trackAction: (action: string) => Promise<void>;
  /** Show a custom gamification toast */
  addToast: (toast: Omit<GamificationToastData, "id">) => void;
  /** Active toast queue */
  toasts: GamificationToastData[];
  /** Dismiss a toast by id */
  dismissToast: (id: string) => void;
  /** Refresh gamification data */
  refresh: () => Promise<void>;
  /** Active floating XP animations */
  xpAnimations: XPAnimationData[];
  /** Whether the level-up celebration overlay is active */
  levelUpActive: boolean;
  /** Dismiss the level-up celebration */
  dismissLevelUp: () => void;
}

const GamificationCtx = createContext<GamificationContextValue | null>(null);

export function useGamification() {
  const ctx = useContext(GamificationCtx);
  if (!ctx) throw new Error("useGamification must be used within GamificationProvider");
  return ctx;
}

/** Safe version that returns null outside the provider (for optional use) */
export function useGamificationOptional() {
  return useContext(GamificationCtx);
}

export function GamificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { features } = useBetaFeatures();
  // Pre-beta feature freeze (sw-b7h): when flag OFF, suppress all gamification
  // — no fetching, no XP, no badges, no toasts, no animations, paused streaks.
  const enabled = features.gamification;
  const queryClient = useQueryClient();
  const [toasts, setToasts] = useState<GamificationToastData[]>([]);
  const [xpAnimations, setXPAnimations] = useState<XPAnimationData[]>([]);
  const [levelUpActive, setLevelUpActive] = useState(false);
  const toastIdRef = useRef(0);
  const animIdRef = useRef(0);

  const userId = user?.id ?? "";

  const { data: gamification = null } = useQuery({
    queryKey: queryKeys.gamification(userId),
    queryFn: getUserGamification,
    enabled: !!user && enabled,
  });

  const gamificationRef = useRef(gamification);
  gamificationRef.current = gamification;

  const refresh = useCallback(async () => {
    if (!user) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.gamification(userId) });
  }, [user, userId, queryClient]);

  const addToast = useCallback(
    (toast: Omit<GamificationToastData, "id">) => {
      if (!enabled) return;
      const id = `gam-toast-${++toastIdRef.current}`;
      setToasts((prev) => [...prev, { ...toast, id }]);
      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 5000);
    },
    [enabled],
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissLevelUp = useCallback(() => {
    setLevelUpActive(false);
  }, []);

  const addXPAnimation = useCallback((amount: number) => {
    const id = `xp-anim-${++animIdRef.current}`;
    setXPAnimations((prev) => [...prev, { id, amount }]);
    setTimeout(() => {
      setXPAnimations((prev) => prev.filter((a) => a.id !== id));
    }, 1400);
  }, []);

  // Streak-at-risk toast: show on load if last activity was yesterday and streak >= 3
  const streakToastShown = useRef(false);
  useEffect(() => {
    if (!enabled || !gamification || streakToastShown.current) return;
    const { last_activity_date, current_streak } = gamification;
    if (!last_activity_date || current_streak < 3) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastActivity = new Date(last_activity_date + "T00:00:00");
    const diffMs = today.getTime() - lastActivity.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      streakToastShown.current = true;
      addToast({
        type: "streak_at_risk",
        title: "Streak at risk!",
        description: `Your ${current_streak}-day streak is alive! Do one action to keep it going.`,
        icon: "flame",
      });
    }
  }, [enabled, gamification, addToast]);

  const trackAction = useCallback(
    async (action: string) => {
      if (!user || !enabled) return;
      try {
        const prevLevel = gamificationRef.current?.level ?? 1;
        const prevXP = gamificationRef.current?.xp_points ?? 0;
        const updated = await awardXP(action);
        queryClient.setQueryData(queryKeys.gamification(userId), updated);

        // Floating XP animation
        const xpDelta = updated.xp_points - prevXP;
        if (xpDelta > 0) {
          addXPAnimation(xpDelta);
        }

        // Check for level up
        if (updated.level > prevLevel) {
          const levelLabel = LEVEL_LABELS[updated.level] ?? `Level ${updated.level}`;
          setLevelUpActive(true);
          addToast({
            type: "level_up",
            title: `Level Up! ${levelLabel}`,
            description: `You reached Level ${updated.level}!`,
            icon: "trophy",
          });
        } else {
          // Check for proximity nudge (close to next level but didn't level up)
          const nudge = getProximityNudge(updated.xp_points, updated.level);
          if (nudge) {
            addToast({
              type: "proximity",
              title: `Just ${nudge.xpRemaining} XP from ${nudge.nextLevelLabel}!`,
              description: `Almost there — ${nudge.suggestion}!`,
              icon: "flame",
            });
          }
        }

        // Check and award badges
        const newBadges = await checkAndAwardBadges();
        for (const badgeId of newBadges) {
          const badge = getBadgeDefinition(badgeId);
          if (badge) {
            addToast({
              type: "badge",
              title: `Badge Earned: ${badge.name}`,
              description: badge.description,
              icon: badge.icon,
            });
          }
        }

        // Refresh state after badges
        if (newBadges.length > 0) {
          await queryClient.invalidateQueries({ queryKey: queryKeys.gamification(userId) });
        }
      } catch {
        // Non-critical — don't break the main action
      }
    },
    [user, userId, enabled, addToast, addXPAnimation, queryClient],
  );

  const contextValue = useMemo<GamificationContextValue>(
    () => ({
      gamification,
      trackAction,
      addToast,
      toasts,
      dismissToast,
      refresh,
      xpAnimations,
      levelUpActive,
      dismissLevelUp,
    }),
    [
      gamification,
      trackAction,
      addToast,
      toasts,
      dismissToast,
      refresh,
      xpAnimations,
      levelUpActive,
      dismissLevelUp,
    ],
  );

  // Pre-beta feature freeze (sw-b7h): when gamification flag OFF, expose a
  // null context so every consumer of useGamificationOptional() short-circuits.
  // GamificationWidget, NavGamificationIndicator, UserProfileDropdown level
  // panel, GamificationToastContainer, XPFloatLayer, and LevelUpCelebration
  // all already early-return on null context — single switch flips them all.
  return (
    <GamificationCtx.Provider value={enabled ? contextValue : null}>
      {children}
    </GamificationCtx.Provider>
  );
}
